
import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export interface ChatScrollAreaProps {
  children: React.ReactNode;
  className?: string;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  onScrollNearTop?: (isNear: boolean) => void;
  onScrollNearBottom?: (isNear: boolean) => void;
  bottomThreshold?: number;
  topThreshold?: number;
}

export interface ChatScrollAreaRef {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  scrollToTop: (behavior?: ScrollBehavior) => void;
  scrollToElement: (elementId: string, behavior?: ScrollBehavior) => void;
  getViewportElement: () => HTMLElement | null;
  isNearBottom: boolean;
  isNearTop: boolean;
}

export const ChatScrollArea = forwardRef<ChatScrollAreaRef, ChatScrollAreaProps>(
  ({ 
    children, 
    className, 
    onScroll, 
    onScrollNearTop, 
    onScrollNearBottom, 
    bottomThreshold = 200,
    topThreshold = 100
  }, ref) => {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const [isNearBottom, setIsNearBottom] = useState(true);
    const [isNearTop, setIsNearTop] = useState(true);
    const scrollCallbackRef = useRef<number>();
    const isMobile = useIsMobile();
    
    // Expose scroll methods to parent components
    useImperativeHandle(ref, () => ({
      scrollToBottom: (behavior: ScrollBehavior = 'smooth') => {
        console.log('ChatScrollArea scrollToBottom called');
        const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        if (viewport) {
          const scrollHeight = viewport.scrollHeight;
          viewport.style.scrollBehavior = behavior;
          viewport.scrollTop = scrollHeight;
          console.log('Scrolled to bottom, scrollHeight:', scrollHeight);
          // Reset to default after scroll
          setTimeout(() => {
            viewport.style.scrollBehavior = '';
          }, 300);
        } else {
          console.log('Viewport not found for scrollToBottom');
        }
      },
      scrollToTop: (behavior: ScrollBehavior = 'smooth') => {
        const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        if (viewport) {
          viewport.style.scrollBehavior = behavior;
          viewport.scrollTop = 0;
          // Reset to default after scroll
          setTimeout(() => {
            viewport.style.scrollBehavior = '';
          }, 300);
        }
      },
      scrollToElement: (elementId: string, behavior: ScrollBehavior = 'smooth') => {
        const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        const element = document.getElementById(elementId);
        if (viewport && element) {
          const elementRect = element.getBoundingClientRect();
          const viewportRect = viewport.getBoundingClientRect();
          const scrollOffset = element.offsetTop - viewportRect.height / 2 + elementRect.height / 2;
          
          viewport.style.scrollBehavior = behavior;
          viewport.scrollTop = scrollOffset;
          // Reset to default after scroll
          setTimeout(() => {
            viewport.style.scrollBehavior = '';
          }, 300);
        }
      },
      getViewportElement: () => {
        return scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      },
      isNearBottom,
      isNearTop
    }));

    // Throttled scroll handler for better performance
    const throttledScrollHandler = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      if (scrollCallbackRef.current) {
        cancelAnimationFrame(scrollCallbackRef.current);
      }
      
      scrollCallbackRef.current = requestAnimationFrame(() => {
        const viewport = e.currentTarget;
        const scrollTop = viewport.scrollTop;
        const clientHeight = viewport.clientHeight;
        const scrollHeight = viewport.scrollHeight;
        
        // Check if we're near the bottom
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const newIsNearBottom = distanceFromBottom < bottomThreshold;
        
        if (newIsNearBottom !== isNearBottom) {
          console.log('isNearBottom changed:', newIsNearBottom, 'distance:', distanceFromBottom);
          setIsNearBottom(newIsNearBottom);
          if (onScrollNearBottom) onScrollNearBottom(newIsNearBottom);
        }
        
        // Check if we're near the top
        const newIsNearTop = scrollTop < topThreshold;
        if (newIsNearTop !== isNearTop) {
          setIsNearTop(newIsNearTop);
          if (onScrollNearTop) onScrollNearTop(newIsNearTop);
        }
        
        // Forward the scroll event
        if (onScroll) onScroll(e);
      });
    }, [bottomThreshold, topThreshold, isNearBottom, isNearTop, onScrollNearBottom, onScrollNearTop, onScroll]);

    // Apply optimizations for scrolling
    useEffect(() => {
      const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (viewport) {
        // Enhanced mobile-specific optimizations
        if (isMobile) {
          // Critical: Enable native smooth scrolling on iOS
          (viewport.style as any).webkitOverflowScrolling = 'touch';
          // Prevent overscroll behavior that can interfere
          viewport.style.overscrollBehavior = 'contain';
          // Enable hardware acceleration
          viewport.style.willChange = 'scroll-position';
          // Ensure proper touch handling
          viewport.style.touchAction = 'pan-y';
          // Remove any transform that might interfere
          viewport.style.transform = 'none';
          // iOS-specific keyboard handling
          viewport.classList.add('ios-keyboard-fix');
        } else {
          // Desktop optimizations
          viewport.style.willChange = 'transform, scroll-position';
          viewport.style.transform = 'translateZ(0)';
        }
        
        // Ensure proper event handling - CRITICAL FIX
        viewport.style.pointerEvents = 'auto';
        
        // Enhanced passive scroll listener
        const passiveScroll = (e: Event) => {
          const target = e.target as HTMLElement;
          const syntheticEvent = {
            currentTarget: target,
            target,
            preventDefault: () => {},
            stopPropagation: () => {}
          } as unknown as React.UIEvent<HTMLDivElement>;
          
          throttledScrollHandler(syntheticEvent);
        };
        
        viewport.addEventListener('scroll', passiveScroll, { 
          passive: true,
          capture: false
        });
        
        // Initial scroll state check with delay for mobile
        const checkInitialState = () => {
          const scrollHeight = viewport.scrollHeight;
          const clientHeight = viewport.clientHeight;
          const scrollTop = viewport.scrollTop;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          const isAtBottom = distanceFromBottom < bottomThreshold;
          console.log('Initial scroll state:', { scrollHeight, clientHeight, scrollTop, distanceFromBottom, isAtBottom });
          setIsNearBottom(isAtBottom);
          if (onScrollNearBottom) onScrollNearBottom(isAtBottom);
        };
        
        // Use longer delay on mobile for better reliability
        setTimeout(checkInitialState, isMobile ? 300 : 200);
        
        return () => {
          viewport.removeEventListener('scroll', passiveScroll);
          if (scrollCallbackRef.current) {
            cancelAnimationFrame(scrollCallbackRef.current);
          }
        };
      }
    }, [throttledScrollHandler, isMobile, bottomThreshold, onScrollNearBottom]);

    return (
      <ScrollArea 
        ref={scrollAreaRef} 
        className={cn(
          "h-full overflow-hidden",
          // Enhanced mobile scroll classes
          isMobile && "momentum-scroll ios-scroll-fix",
          className
        )}
        style={{
          // CRITICAL: Ensure scroll area can receive touch events
          pointerEvents: 'auto',
          // Proper positioning
          position: 'relative',
          // Reduced z-index to avoid conflicts
          zIndex: 1,
          // Mobile-specific touch optimizations
          ...(isMobile && {
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          })
        }}
      >
        <div 
          className={cn(
            "will-change-scroll",
            // Add keyboard awareness for mobile
            isMobile && "animate-keyboard-aware"
          )}
          data-chat-scroll-content
          style={{
            // Ensure content can receive touch events
            pointerEvents: 'auto',
            // Mobile optimization: prevent text selection during scroll
            ...(isMobile && {
              userSelect: 'none',
              WebkitUserSelect: 'none',
              // Smooth transitions for keyboard animations
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            })
          }}
        >
          {children}
        </div>
      </ScrollArea>
    );
  }
);

ChatScrollArea.displayName = "ChatScrollArea";
